import { useState, useEffect } from 'react'
import { CAMPAIGN_TYPE_OPTIONS } from '../constants/campaignTypes'
import { getDashboardOverview, DashboardFilters } from '../services/api'
import MetricCard from '../components/MetricCard'
import LoadingSpinner from '../components/LoadingSpinner'

interface DashboardData {
  summary: {
    totalLeads: number
    totalSpend: number
    totalRevenue: number
    profit: number
  }
  kpis: {
    roi: number
    cpl: number
    rpl: number
    profitMargin: number
    validationRate: number
  }
  channels: {
    google: { spend: number; percentage: number }
    microsoft: { spend: number; percentage: number }
    facebook: { spend: number; percentage: number }
    tiktok: { spend: number; percentage: number }
  }
  metadata: {
    dataSource: string
    totalRecords: number
    lastUpdated: string
    currency: string
  }
}

const Media = () => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    source: 'all',
    type: 'all',
    flow: 'all'
  })

  const fetchData = async (appliedFilters?: DashboardFilters) => {
    try {
      setLoading(true)
      
      const filtersToUse = appliedFilters || {
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        type: filters.type,
        flow: filters.flow
      }
      
      const response = await getDashboardOverview(filtersToUse)
      setData(response.data)
      setError(null)
      
      console.log('📺 Datos de medios cargados con filtros:', filtersToUse)
    } catch (err) {
      setError('Error al cargar los datos de medios')
      console.error('Error fetching media data:', err)
    } finally {
      setLoading(false)
    }
  }

  const resetAllFilters = () => {
    console.log('Reseteando todos los filtros')
    const defaultFilters = {
      startDate: '2025-09-01',
      endDate: '2025-11-30',
      source: 'all',
      type: 'all',
      flow: 'all'
    }
    setFilters(defaultFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: defaultFilters.startDate,
      endDate: defaultFilters.endDate,
      source: defaultFilters.source,
      type: defaultFilters.type,
      flow: defaultFilters.flow
    }
    fetchData(apiFilters)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const newFilters = { ...filters, startDate, endDate }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate,
      endDate,
      source: newFilters.source,
      type: newFilters.type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleSourceChange = (source: string) => {
    const newFilters = { ...filters, source }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source,
      type: newFilters.type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleTypeChange = (type: string) => {
    const newFilters = { ...filters, type }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: newFilters.source,
      type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleFlowChange = (flow: string) => {
    const newFilters = { ...filters, flow }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: newFilters.source,
      type: newFilters.type,
      flow
    }
    fetchData(apiFilters)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <div>No hay datos disponibles</div>
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">📺</span>
            Media Overview
          </h1>
          <p className="text-dashboard-muted mt-1">
            Vista consolidada del rendimiento de medios digitales
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-dashboard-muted">
            Última actualización
          </div>
          <div className="text-sm font-semibold text-dashboard-text">
            {new Date(data.metadata.lastUpdated).toLocaleString('es-ES')}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filter-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-dashboard-text">Filtros de Análisis</h3>
          <button 
            onClick={resetAllFilters}
            className="btn-secondary text-sm"
          >
            Resetear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleDateRangeChange(e.target.value, filters.endDate)}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleDateRangeChange(filters.startDate, e.target.value)}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fuente
            </label>
            <select
              value={filters.source}
              onChange={(e) => handleSourceChange(e.target.value)}
              className="filter-select w-full"
            >
              <option value="all">Todas las fuentes</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="microsoft">Microsoft</option>
              <option value="tiktok">TikTok</option>
              <option value="leadcentre">Leadcentre</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Tipo
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="filter-select w-full"
            >
              {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Flow
            </label>
            <select
              value={filters.flow}
              onChange={(e) => handleFlowChange(e.target.value)}
              className="filter-select w-full"
            >
              <option value="all">Todos los flows</option>
              <option value="seguro-salud">Seguro Salud</option>
              <option value="creditos-personales">Créditos Personales</option>
              <option value="tarjeta-credito">Tarjeta de Crédito</option>
              <option value="seguro-decesos">Seguro Decesos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Resumen de Medios - solo mostrar si no hay filtro específico */}
      {filters.source === 'all' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <MetricCard
            title="Google Ads"
            value={`€${data.channels.google.spend.toFixed(2)}`}
            icon="🔍"
            subtitle={`${data.channels.google.percentage.toFixed(1)}% del total`}
            premium={data.channels.google.percentage > 30}
          />
          <MetricCard
            title="Microsoft Ads"
            value={`€${data.channels.microsoft.spend.toFixed(2)}`}
            icon="🟦"
            subtitle={`${data.channels.microsoft.percentage.toFixed(1)}% del total`}
            premium={data.channels.microsoft.percentage > 30}
          />
          <MetricCard
            title="Facebook Ads"
            value={`€${data.channels.facebook.spend.toFixed(2)}`}
            icon="📘"
            subtitle={`${data.channels.facebook.percentage.toFixed(1)}% del total`}
            premium={data.channels.facebook.percentage > 30}
          />
          <MetricCard
            title="TikTok Ads"
            value={`€${data.channels.tiktok.spend.toFixed(2)}`}
            icon="🎵"
            subtitle={`${data.channels.tiktok.percentage.toFixed(1)}% del total`}
            premium={data.channels.tiktok.percentage > 30}
          />
          <MetricCard
            title="Leadcentre"
            value={`€${(data.summary.totalRevenue * 0.3).toFixed(2)}`}
            icon="🎯"
            subtitle={`${((data.summary.totalRevenue * 0.3) / data.summary.totalSpend * 100).toFixed(1)}% del total`}
            premium={((data.summary.totalRevenue * 0.3) / data.summary.totalSpend * 100) > 30}
          />
        </div>
      )}

      {/* Distribución de Inversión - solo mostrar si no hay filtro específico */}
      {filters.source === 'all' && (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-dashboard-text mb-6 flex items-center">
            <span className="mr-2">📊</span>
            Distribución de Inversión por Canal
          </h2>
        <div className="space-y-4">
          {/* Mostrar Google solo si no hay filtro o si el filtro es google */}
          {(filters.source === 'all' || filters.source === 'google') && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <span className="text-xl">🔍</span>
                <span className="text-sm font-medium text-dashboard-text">Google Ads</span>
              </div>
              <div className="flex-1">
                <div className="w-full bg-dashboard-border-light rounded-full h-3">
                  <div 
                    className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${data.channels.google.percentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-right w-24">
                <div className="text-sm font-bold text-dashboard-text">
                  €{data.channels.google.spend.toFixed(2)}
                </div>
                <div className="text-xs text-dashboard-muted">
                  {data.channels.google.percentage.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
          
          {/* Mostrar Microsoft solo si no hay filtro o si el filtro es microsoft */}
          {(filters.source === 'all' || filters.source === 'microsoft') && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <span className="text-xl">🟦</span>
                <span className="text-sm font-medium text-dashboard-text">Microsoft Ads</span>
              </div>
              <div className="flex-1">
                <div className="w-full bg-dashboard-border-light rounded-full h-3">
                  <div 
                    className="bg-orange-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${data.channels.microsoft.percentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-right w-24">
                <div className="text-sm font-bold text-dashboard-text">
                  €{data.channels.microsoft.spend.toFixed(2)}
                </div>
                <div className="text-xs text-dashboard-muted">
                  {data.channels.microsoft.percentage.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
          
          {/* Mostrar Facebook solo si no hay filtro o si el filtro es facebook */}
          {(filters.source === 'all' || filters.source === 'facebook') && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <span className="text-xl">📘</span>
                <span className="text-sm font-medium text-dashboard-text">Facebook Ads</span>
              </div>
              <div className="flex-1">
                <div className="w-full bg-dashboard-border-light rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${data.channels.facebook.percentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-right w-24">
                <div className="text-sm font-bold text-dashboard-text">
                  €{data.channels.facebook.spend.toFixed(2)}
                </div>
                <div className="text-xs text-dashboard-muted">
                  {data.channels.facebook.percentage.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
          
          {/* Mostrar TikTok solo si no hay filtro o si el filtro es tiktok */}
          {(filters.source === 'all' || filters.source === 'tiktok') && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <span className="text-xl">🎵</span>
                <span className="text-sm font-medium text-dashboard-text">TikTok Ads</span>
              </div>
              <div className="flex-1">
                <div className="w-full bg-dashboard-border-light rounded-full h-3">
                  <div 
                    className="bg-black h-3 rounded-full transition-all duration-500"
                    style={{ width: `${data.channels.tiktok.percentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-right w-24">
                <div className="text-sm font-bold text-dashboard-text">
                  €{data.channels.tiktok.spend.toFixed(2)}
                </div>
                <div className="text-xs text-dashboard-muted">
                  {data.channels.tiktok.percentage.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
          
          {/* Mostrar Leadcentre solo si no hay filtro o si el filtro es leadcentre */}
          {(filters.source === 'all' || filters.source === 'leadcentre') && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <span className="text-xl">🎯</span>
                <span className="text-sm font-medium text-dashboard-text">Leadcentre</span>
              </div>
              <div className="flex-1">
                <div className="w-full bg-dashboard-border-light rounded-full h-3">
                  <div 
                    className="bg-purple-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${((data.summary.totalRevenue * 0.3) / data.summary.totalSpend * 100)}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-right w-24">
                <div className="text-sm font-bold text-dashboard-text">
                  €{(data.summary.totalRevenue * 0.3).toFixed(2)}
                </div>
                <div className="text-xs text-dashboard-muted">
                  {((data.summary.totalRevenue * 0.3) / data.summary.totalSpend * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Métricas Consolidadas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Inversión Total"
          value={`€${data.summary.totalSpend.toFixed(2)}`}
          icon="💰"
          subtitle="Gasto total en medios"
          premium={true}
        />
        <MetricCard
          title="Revenue Total"
          value={`€${data.summary.totalRevenue.toFixed(2)}`}
          icon="💵"
          subtitle="Ingresos generados"
          premium={data.summary.totalRevenue > data.summary.totalSpend}
        />
        <MetricCard
          title="Beneficio"
          value={`€${data.summary.profit.toFixed(2)}`}
          icon="📊"
          trend={data.summary.profit >= 0 ? "positive" : "negative"}
          subtitle="Revenue - Inversión"
        />
        <MetricCard
          title="Leads Generados"
          value={data.summary.totalLeads.toLocaleString()}
          icon="👥"
          subtitle="Total de leads"
        />
        <MetricCard
          title="CPL"
          value={`€${data.kpis.cpl.toFixed(2)}`}
          icon="🎯"
          subtitle="Cost Per Lead"
        />
        <MetricCard
          title="RPL"
          value={`€${data.kpis.rpl.toFixed(2)}`}
          icon="💎"
          subtitle="Revenue Per Lead"
        />
        <MetricCard
          title="ROI"
          value={`${data.kpis.roi.toFixed(1)}%`}
          icon="📈"
          trend={data.kpis.roi >= 0 ? "positive" : "negative"}
          subtitle="Return on Investment"
        />
        <MetricCard
          title="Margen Beneficio"
          value={`${data.kpis.profitMargin.toFixed(1)}%`}
          icon="📊"
          trend={data.kpis.profitMargin >= 0 ? "positive" : "negative"}
          subtitle="Profit Margin"
        />
        <MetricCard
          title="Tasa Validación"
          value={`${(data.kpis.validationRate * 100).toFixed(1)}%`}
          icon="✅"
          subtitle="Conversion Rate"
          premium={data.kpis.validationRate > 0.8}
        />
      </div>
    </div>
  )
}

export default Media
